import { resolveHexaFill } from '../color';
import { buildPolygonPoints } from '../geometry/build-polygon-points';
import { createSvgElement } from '../svg';
import type { MapFieldThemeModel, MapRenderFieldModel } from '../types';

/**
 * Builds the SVG group for all field polygons in a map scene.
 *
 * @param params Fields and status themes from the render model.
 * @returns Detached group carrying the stable `data-map-layer="fields"` hook.
 */
export function renderFieldsLayer(params: {
  fields: readonly MapRenderFieldModel[];
  fieldStatusTheme: Readonly<Record<MapRenderFieldModel['status'], MapFieldThemeModel>>;
}): SVGGElement {
  const layerElement = createSvgElement('g');
  layerElement.dataset.mapLayer = 'fields';

  for (const field of params.fields) {
    layerElement.append(
      createFieldElement({
        field,
        theme: params.fieldStatusTheme[field.status],
      }),
    );
  }

  return layerElement;
}

/**
 * Builds one semantic field polygon element for full rendering or a partial field update.
 *
 * @param params Field visual data and already-resolved status theme.
 * @returns Detached polygon with the canonical field `data-map-entity-id` hook.
 */
export function createFieldElement(params: {
  field: MapRenderFieldModel;
  theme: MapFieldThemeModel;
}): SVGPolygonElement {
  const color = resolveHexaFill(params.theme.fillColor);
  const polygonElement = createSvgElement('polygon');
  polygonElement.setAttribute('points', buildPolygonPoints(params.field.points));
  polygonElement.setAttribute('fill', color.fill);
  polygonElement.setAttribute('fill-opacity', String(color.fillOpacity));
  polygonElement.setAttribute('stroke', params.theme.stroke);
  polygonElement.setAttribute('stroke-width', '1');
  polygonElement.setAttribute('vector-effect', 'non-scaling-stroke');
  polygonElement.setAttribute('aria-hidden', 'true');
  polygonElement.dataset.mapEntityKind = 'field';
  polygonElement.dataset.mapEntityId = String(params.field.id);

  return polygonElement;
}
